/**
 * Blog Management - Main component for managing blog posts in Geppetto
 * Provides list view, create/edit, and category management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  SimpleGrid,
  useToast
} from '@chakra-ui/react';
import { getBlogStats } from '../services/blogService';
import BlogPostsList from './Blog/BlogPostsList';
import BlogPostEditor from './Blog/BlogPostEditor';
import BlogCategories from './Blog/BlogCategories';

const BlogManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    totalViews: 0,
    categoriesCount: 0
  });
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const toast = useToast();

  // Fetch stats on mount and refresh
  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      const data = await getBlogStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading blog stats:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingPostId(null);
    setActiveTab(1); // Switch to editor tab
  };

  const handleEditPost = (postId: string) => {
    setEditingPostId(postId);
    setActiveTab(1); // Switch to editor tab
  };

  const handlePostSaved = () => {
    setRefreshTrigger(prev => prev + 1);
    setActiveTab(0); // Go back to list
    toast({
      title: 'Success',
      description: 'Blog post saved successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handlePostDeleted = () => {
    setRefreshTrigger(prev => prev + 1);
    toast({
      title: 'Success',
      description: 'Blog post deleted successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCategoryChange = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading size="lg" mb={6}>
          Blog Management
        </Heading>

        {/* Stats Overview */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
          <Stat
            px={4}
            py={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
          >
            <StatLabel>Total Posts</StatLabel>
            <StatNumber>{stats.totalPosts}</StatNumber>
          </Stat>

          <Stat
            px={4}
            py={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
          >
            <StatLabel>Published</StatLabel>
            <StatNumber color="green.500">{stats.publishedPosts}</StatNumber>
          </Stat>

          <Stat
            px={4}
            py={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
          >
            <StatLabel>Drafts</StatLabel>
            <StatNumber color="orange.500">{stats.draftPosts}</StatNumber>
          </Stat>

          <Stat
            px={4}
            py={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
          >
            <StatLabel>Total Views</StatLabel>
            <StatNumber color="blue.500">{stats.totalViews.toLocaleString()}</StatNumber>
          </Stat>

          <Stat
            px={4}
            py={3}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
          >
            <StatLabel>Categories</StatLabel>
            <StatNumber>{stats.categoriesCount}</StatNumber>
          </Stat>
        </SimpleGrid>

        {/* Main Tabs */}
        <Tabs
          index={activeTab}
          onChange={setActiveTab}
          colorScheme="teal"
          variant="enclosed"
        >
          <TabList>
            <Tab>
              All Posts
            </Tab>
            <Tab>
              {editingPostId ? 'Edit Post' : 'New Post'}
            </Tab>
            <Tab>
              Categories
            </Tab>
          </TabList>

          <TabPanels>
            {/* Posts List Tab */}
            <TabPanel p={0} pt={4}>
              <Box mb={4}>
                <Button
                  colorScheme="teal"
                  onClick={handleCreateNew}
                >
                  + Create New Post
                </Button>
              </Box>
              <BlogPostsList
                onEdit={handleEditPost}
                onDelete={handlePostDeleted}
                refreshTrigger={refreshTrigger}
              />
            </TabPanel>

            {/* Editor Tab */}
            <TabPanel p={0} pt={4}>
              <BlogPostEditor
                postId={editingPostId}
                onSave={handlePostSaved}
                onCancel={() => setActiveTab(0)}
              />
            </TabPanel>

            {/* Categories Tab */}
            <TabPanel p={0} pt={4}>
              <BlogCategories onChange={handleCategoryChange} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Container>
  );
};

export default BlogManagement;
